import React, { useState, useRef } from 'react';
import { Upload, X, Image, Video, FileText, Music, Plus, Trash2, Eye, Edit2 } from 'lucide-react';
import { MediaFile } from '../types/socialMedia.types';

interface MediaUploaderProps {
  media: MediaFile[];
  onChange: (media: MediaFile[]) => void;
  disabled?: boolean;
  maxFiles?: number;
  acceptedTypes?: string;
  platformName?: string;
}

export default function MediaUploader({
  media = [],
  onChange,
  disabled = false,
  maxFiles = 10,
  acceptedTypes = 'image/*,video/*',
  platformName = ''
}: MediaUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [editingAltText, setEditingAltText] = useState<string | null>(null);
  const [altTextValue, setAltTextValue] = useState('');

  // Handle file selection
  const handleFileSelect = (files: FileList | null) => {
    if (!files || disabled) return;

    const newMedia: MediaFile[] = [];
    const remainingSlots = maxFiles - media.length;

    for (let i = 0; i < Math.min(files.length, remainingSlots); i++) {
      const file = files[i];
      const fileType = getFileType(file.type);

      // Create a preview URL for the file
      const url = URL.createObjectURL(file);

      newMedia.push({
        id: `${Date.now()}-${i}`,
        url,
        type: fileType,
        name: file.name,
        size: file.size,
        altText: ''
      });
    }

    onChange([...media, ...newMedia]);
  };

  // Determine file type
  const getFileType = (mimeType: string): MediaFile['type'] => {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.includes('gif')) return 'gif';
    return 'document';
  };

  // Handle drag and drop
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  // Remove media file
  const removeMedia = (id: string) => {
    const file = media.find(m => m.id === id);
    if (file?.url.startsWith('blob:')) {
      URL.revokeObjectURL(file.url);
    }
    onChange(media.filter(m => m.id !== id));
  };

  // Update alt text
  const updateAltText = (id: string, altText: string) => {
    onChange(media.map(m =>
      m.id === id ? { ...m, altText } : m
    ));
    setEditingAltText(null);
    setAltTextValue('');
  };

  // Get icon for file type
  const getFileIcon = (type: MediaFile['type']) => {
    switch (type) {
      case 'image':
      case 'gif':
        return Image;
      case 'video':
        return Video;
      default:
        return FileText;
    }
  };

  // Format file size
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      {media.length < maxFiles && (
        <div
          className={`
            relative border-2 border-dashed rounded-lg p-6
            transition-colors cursor-pointer
            ${dragActive
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400 bg-gray-50'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => !disabled && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={acceptedTypes}
            onChange={(e) => handleFileSelect(e.target.files)}
            disabled={disabled}
            className="hidden"
          />

          <div className="text-center">
            <Upload className="w-10 h-10 mx-auto text-gray-400 mb-3" />
            <p className="text-sm font-medium text-gray-700">
              Click to upload or drag and drop
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {platformName && `Add media to your ${platformName} post`}
            </p>
            <p className="text-xs text-gray-400 mt-2">
              {media.length} / {maxFiles} files added
            </p>
          </div>
        </div>
      )}

      {/* Media Grid */}
      {media.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {media.map((file) => {
            const Icon = getFileIcon(file.type);
            const isImage = file.type === 'image' || file.type === 'gif';

            return (
              <div
                key={file.id}
                className="relative group bg-white border rounded-lg overflow-hidden"
              >
                {/* Preview */}
                {isImage ? (
                  <img
                    src={file.url}
                    alt={file.altText || file.name}
                    className="w-full h-32 object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-full h-32 flex items-center justify-center bg-gray-100">
                    <Icon className="w-12 h-12 text-gray-400" />
                  </div>
                )}

                {/* File Info Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <div className="flex space-x-2">
                    {isImage && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingAltText(file.id || '');
                          setAltTextValue(file.altText || '');
                        }}
                        disabled={disabled}
                        className="p-2 bg-white rounded-full hover:bg-gray-100 disabled:opacity-50"
                        title="Edit alt text"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(file.url, '_blank');
                      }}
                      className="p-2 bg-white rounded-full hover:bg-gray-100"
                      title="View full size"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeMedia(file.id || '');
                      }}
                      disabled={disabled}
                      className="p-2 bg-white rounded-full hover:bg-red-100 disabled:opacity-50"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>

                {/* File Details */}
                <div className="p-2 bg-white">
                  <p className="text-xs font-medium truncate">{file.name}</p>
                  <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                  {file.altText && (
                    <p className="text-xs text-green-600 truncate mt-1">
                      Alt: {file.altText}
                    </p>
                  )}
                </div>

                {/* Alt Text Editor */}
                {editingAltText === file.id && (
                  <div className="absolute inset-0 bg-white p-3 flex flex-col">
                    <label className="text-xs font-medium text-gray-700 mb-1">
                      Alt Text
                    </label>
                    <input
                      type="text"
                      value={altTextValue}
                      onChange={(e) => setAltTextValue(e.target.value)}
                      className="flex-1 px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Describe image..."
                      autoFocus
                    />
                    <div className="flex justify-end space-x-2 mt-2">
                      <button
                        onClick={() => {
                          setEditingAltText(null);
                          setAltTextValue('');
                        }}
                        className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => updateAltText(file.id || '', altTextValue)}
                        className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Add More Button */}
          {media.length < maxFiles && (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled}
              className="h-32 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 flex items-center justify-center group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="text-center">
                <Plus className="w-8 h-8 text-gray-400 group-hover:text-gray-600 mx-auto" />
                <span className="text-xs text-gray-500 mt-1">Add More</span>
              </div>
            </button>
          )}
        </div>
      )}

      {/* Platform-specific tips */}
      <div className="text-xs text-gray-500 space-y-1">
        {platformName === 'Instagram' && (
          <>
            <p>• Carousel posts: Add up to 10 images/videos</p>
            <p>• Best image size: 1080x1080px (square) or 1080x1350px (portrait)</p>
          </>
        )}
        {platformName === 'Facebook' && (
          <>
            <p>• Supports multiple images for carousel posts</p>
            <p>• Videos should be under 240 minutes</p>
          </>
        )}
        {platformName === 'X' && (
          <>
            <p>• Up to 4 images per tweet</p>
            <p>• GIFs and videos count as 1 media item</p>
          </>
        )}
        {platformName === 'LinkedIn' && (
          <>
            <p>• Supports images, videos, and documents</p>
            <p>• Videos should be between 3 seconds and 10 minutes</p>
          </>
        )}
      </div>
    </div>
  );
}